import React, { useContext, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRight, faChartLine, faCheck, faClose, faCopy,
  faGlobe, faHandshake, faLockOpen, faQuestion, faRobot, faRocket,
} from "@fortawesome/free-solid-svg-icons";
import { cn } from "../../../lib/helpers";
import { pageDocPath } from "../../../../helpers";
import PortableText from "../../portableText";
import { Delay } from "../../../templates/account";
import { ImageBuilder } from "../../global/image-builder";
import { SiteContext } from "../../global/site-context";
import { PillHeader } from "../wrapper";
import { TinyPills } from "../../card/card";
import { typesMap } from "../resources-list";
import ReactTooltip from "react-tooltip";
import { ATTRIBUTION_PARAM, SIGNUP_PHRASE } from "./navigation";
import { compress, LLM_MODELS } from "./play-finder";
import { CompetitorsGrid } from "./competitors";

import * as pricingStyles from "./pricing-plans.module.css";
import * as styles from "../snowflake/pricing-plans.module.css";

const CARDS_PER_SECTION = 4;
const APPEAR_DELAY = 200;
const SORRY_MESSAGE = "Sorry, we have limited strategic business growth results for this domain right now.";
const SHOW_COMPETETORS = false;

const SECTIONS = [
  {
    title: "Product-led plays",
    why: "We noticed you're product-led, these plays will enhance your existing strategy.",
    id: "product-led-growth",
    conclusion: "isProductLed",
    icon: faRocket,
    cta: "Import product data in minutes via CDP, data warehouse, or API."
  },
  {
    title: "Website activity",
    why: "We noticed you have plenty of traffic, these plays will work well for you.",
    id: "website-visitor-tracking",
    conclusion: "isVisited",
    icon: faGlobe,
    cta: "De-anonymize up to 50% of web visits at the person level."
  },
  {
    title: "Social selling",
    why: "We noticed you have a strong social presence, these plays will leverage that.",
    id: "dark-funnel",
    conclusion: "isSocial",
    icon: faHandshake,
    cta: "Tap into dark funnel signals on LinkedIn, X, Slack, and more."
  },
  {
    title: "Open-source plays",
    why: "We noticed you have an open-source offering, lean into that with these plays.",
    id: "commercial-open-source-software",
    conclusion: "isOpenSource",
    icon: faLockOpen,
    cta: "Capture open source signals from any GitHub repo."
    // @todo include devRel
  },
  {
    title: "Sales-led plays",
    why: "We noticed you have a strong sales team, use these plays to enhance that strategy.",
    id: "sales",
    conclusion: "isSalesLed",
    icon: faChartLine,
    cta: "Track job changes, hiring trends, and news out-of-the-box."
  },
];

const AI_SECTION = {
  title: "AI Selected",
  why: "We let the AI comb though our playbooks and signals to find the best ones for you.",
  id: "",
  conclusion: "",
  icon: faRobot,
  cta: "Hunt signals, orchestrate pipeline, and outbound at scale with RoomieAI™."
};

const DETAILS = [
  {
    label: "Web Traffic",
    placeholder: "unknown",
    score: "3",
    id: "webTraffic",
  },
  {
    label: "Social",
    placeholder: "unknown",
    score: "5",
    id: "social",
  },
  {
    label: "Team",
    placeholder: "unknown",
    id: "team",
  },
  {
    label: "Funding",
    placeholder: "unknown",
    id: "funding",
  },
];

// @todo use confidence score to determine if we should show the section
const shouldIncludeSection = (section, company) =>
  company.conclusions?.[section.conclusion]?.value;

export const Report = ({ content, company, reset }) => {
  const site = useContext(SiteContext);
  const sections = SECTIONS.filter(s => shouldIncludeSection(s, company)).sort((a, b) =>
    company.conclusions[a.conclusion].confidence > company.conclusions[b.conclusion].confidence ? -1 : 1
  );
  const isAppropriate = company.conclusions.isAppropriate.value;
  const details = DETAILS.filter(({ id }) => !!company.details?.[id]);

  return (
    <div className="relative mb-36">
      <div className="flex flex-col sm:flex-row gap-4 sm:gap-8 mb-6">
        <div className="">
          <ImageBuilder
            src={`https://logo.clearbit.com/${company.domain}`}
            alt={company.name}
            width={site.isNotMobile ? 170 : 80}
            className="bg-white p-4 rounded-2xl"
          />
        </div>
        <div className="ml-2 md:mr-12">
          <h2 className="text-light-70 mb-2 text-xl">{company.name} AI plays</h2>
          <p className="text-3xl text-medium leading-snug lg:max-w-3/4">
            {isAppropriate ? company.summary : SORRY_MESSAGE}
          </p>
          {isAppropriate &&
            <Share
              path={`/plays/?c=${encodeURIComponent(compress(company))}`}
              site={site}
            />
          }
        </div>
      </div>
      <p className="my-6 text-sm mx-2 text-light-50 leading-normal transition-colors duration-200">
        Results may vary, powered by <span className="font-medium">{LLM_MODELS}.</span>
      </p>
      {isAppropriate && details.length > 1 && (
        <ul className={cn(
          "grid gap-4 my-12",
          details.length > 3 ? "sm:grid-cols-2 lg:grid-cols-4"
            : details.length === 3 ? "md:grid-cols-3"
              : "grid-cols-2"
        )}>
          {details.map(({ label, placeholder, id }, i) => (
            <li className="bg-dark-20 rounded-lg p-4" key={i}>
              {/* <FontAwesomeIcon icon={faInfoCircle} size="xs" /> */}
              <p className="text-light-70">{label}</p>
              <p className="font-medium">{company.details?.[id] || placeholder}</p>
            </li>
          ))}
        </ul>
      )}
      {SHOW_COMPETETORS && company.details?.competitors && company.details.competitors.length > 0 && (
        <div className="my-12">
          <h3 className="text-2xl font-semibold mb-4">Competitors</h3>
          <CompetitorsGrid competitors={company.details.competitors} company={company} />
        </div>
      )}
      <div className="">
        {sections.map((section, i) => (
          <ContentBlock content={
            content.filter(
              // content tagged with the section category
              c => c.isFeatured && c.categories.find(
                tag => tag.slug.current === section.id
              )
            ).sort(
              // show AI picks first
              (a, _b) => company?.suggest.includes(a._id) ? -1 : 1
            ).slice(0, CARDS_PER_SECTION)
          }
            section={section}
            company={company}
            key={i}
          />
        ))}
        {company?.suggest?.length > 0 && (
          <ContentBlock
            section={AI_SECTION}
            content={content.filter(n => company.suggest.includes(n._id)).slice(0, CARDS_PER_SECTION)}
            company={company}
          />
        )}
      </div>
      <a className="absolute top-0 right-0">
        <FontAwesomeIcon
          icon={faClose}
          size="xl"
          className="text-light-30 cursor-pointer hover:text-light-60 transition-colors duration-200"
          onClick={() => reset()}
        />
      </a>
    </div>
  )
}

const Share = ({ path, site }) => (
  <div className="flex flex-row">
    <a
      href={path}
      onClick={(e) => {
        e.preventDefault();
        navigator.clipboard.writeText(`${process.env.HOST}${path}`);
        site.metrics.logClick("copy-click", "playgen");
      }}
      data-tip="Click to copy url"
      data-for="share-report"
      className="text-link flex flex-row items-center mt-2 text-lg font-semibold transform transition-size scale-100 hover:scale-102 duration-200">
      <FontAwesomeIcon icon={faCopy} className="mr-2" />
      <span>Share this report</span>
    </a>
    <ReactTooltip
      id="share-report"
      borderColor="#000"
      place="bottom"
      effect="solid"
      textColor="#fff"
      backgroundColor="#000000"
      className={cn("text-gl", pricingStyles.toolTipSmall)}
      offset={{ bottom: -2 }}
    />
  </div>
);

const ContentBlock = ({ section, company, content }) => {
  const conclusion = company.conclusions?.[section.conclusion];

  // const integrations = docs.reduce((acc, node) => {
  //   if (node.categories.find(category => category.slug.current === section.id)) {
  //     acc.push(node.integrations);
  //   }
  //   return acc;
  // }, []).flat();

  // const unique = [...new Map(
  //   [].concat(integrations).map(
  //     (p) => [p._id, p]
  //   )
  // ).values()];

  return (
    <div className="grid sm:grid-cols-1/3-2/3 gap-8 border-t border-light-10 pt-16 mt-16">
      <div>
        <div>
          <PillHeader header={section.title} background="white" icon={<FontAwesomeIcon icon={section.icon} />} />
          <p className="leading-normal mt-2 mb-6 font-light">
            {company.sections?.[section.id]?.why || section.why}
          </p>
        </div>
        <div className="flex items-center justify-start mt-6 mb-8">
          {conclusion?.confidence && section.conclusion !== "isTAM"
            && (<Gauge
              value={conclusion.confidence * 100}
              label="Confidence"
              negative={!conclusion.value}
              tooltip={conclusion.reason}
            />)}
        </div>
        <a
          href={`/signup/?${ATTRIBUTION_PARAM}=play-finder--${section.id}`}
          target="_blank"
          className="text-auxiliary font-semibold hover:text-link"
        >
          <p>Run {section.title.toLowerCase()} plays</p>
          <p className="font-light leading-normal max-w-3/4 my-1">{section.cta}</p>
          <p>{SIGNUP_PHRASE} <FontAwesomeIcon icon={faArrowRight} className="ml-1" /></p>
        </a>
      </div>

      <div className="text-lg sm:grid grid-cols-2 gap-4">
        {content.map((node, i) => (
          <Delay delay={(i * APPEAR_DELAY) + (Math.random() * APPEAR_DELAY)} key={i}>
            <Card node={node} company={company} />
          </Delay>
        ))}
      </div>

      {/* {unique && unique.length > 0 && (
        <>
          <p className="text-light-50 mt-2 self-center">
            Signal sources in these plays
          </p>
          <div className={cn(
            "flex flex-wrap gap-y-2 items-center justify-center my-3 mx-auto",
            unique.length > 7 ? "max-w-2/3" : null
          )}>
            <IntegrationsTiles integrations={unique} untitled={true} />
          </div>
        </>
      )} */}
    </div>
  )
};

const Card = ({ node, company }) => (
  <a
    href={pageDocPath(node)}
    target="_blank"
    className="block h-full relative p-4 py-6 pb-16 bg-light-2 hover:bg-light-5 rounded-lg transition-card transform scale-100 hover:scale-102 hover:shadow-lg duration-200"
  >
    <TinyPills list={[node._type]} isDarkBg={true} />
    {company.suggest.find(s => node._id === s)
      ? <p className="text-lemon text-sm absolute top-6 right-6 flex items-center gap-1">
        <FontAwesomeIcon icon={faRobot} className="-mt-1" /> AI pick
      </p>
      : null}
    <p className="font-semibold my-3 text-xl">{node.title}</p>
    <PortableText blocks={node._rawExcerpt} className="text-sm text-light-70 leading-normal mt-1" />
    {/* {node.teams && node.teams.length > 0 && (
      <ul className="text-sm text-light-70">
        {node.teams.map((n, i) => (
          <li className="mb-1" key={i}>
            <FontAwesomeIcon icon={faCheck} size="2xs" className="text-link mr-2 max-h-8 text-auxiliary" />
            {n}
          </li>
        ))}
      </ul>
    )} */}
    <span className="absolute bottom-6 left-4 right-4 text-link text-sm flex gap-2 items-center mt-4">
      {typesMap.get(node._type).cta} <FontAwesomeIcon icon={faArrowRight} />
    </span>
  </a>
)


const Gauge = ({ value, label, negative, tooltip }) => (
  <>
    <div
      className={cn(
        "flex flex-col items-center cursor-pointer group",
        negative || value < 59 ? "text-red" :
          value > 86 ? "text-green-emerald" :
            value > 65 ? "text-lemon" :
              "text-white",
      )}
      data-for="gauge"
      data-tip={tooltip}
    >
      <Dial value={value} negative={negative} />
      <p className="text-light-30 mt-3 text-xs uppercase group-hover:text-white">{label}</p>
    </div>
    <ReactTooltip
      id="gauge"
      borderColor="#000"
      place="right"
      effect="solid"
      textColor="#fff"
      backgroundColor="#000000"
      className={cn("text-lg", styles.toolTip)}
    />
  </>
);

const Dial = ({ value, negative }) => (
  <div className="flex items-center justify-center opacity-50">
    <div className="relative w-24 h-24">
      <svg viewBox="0 0 32 32" className="w-full h-full transform -rotate-90">
        <circle
          cx="16"
          cy="16"
          r="15"
          fill="none"
          stroke="#555"
          strokeWidth="2"
        />
        <circle
          cx="16"
          cy="16"
          r="15"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeDasharray={`${(value / 100) * 94.2} 94.2`}
          strokeDashoffset="0"
        />
      </svg>
      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-2xl font-semibold">
        {value}
      </div>
    </div>
  </div>
);
